//#region IMPORTS
import { InstoreOrderLineTableRow } from "../../models/instoreOrder";
import { useDrag, useDrop } from "react-dnd";
import { flexRender, Row } from "@tanstack/react-table";
import { VirtualItem } from "@tanstack/react-virtual";
import { useRef } from "react";
import classNames from "../../helpers/classNames";
//#endregion

//#region INTERFACE
interface Props {
  virtualRow: VirtualItem;
  moveRow?: (toIndex: number, row: InstoreOrderLineTableRow) => void;
  row: Row<InstoreOrderLineTableRow>;
}
//#endregion

export default function InstoreOrderDetailTableBodyRow({
  virtualRow,
  moveRow,
  row,
}: Props) {
  //#region DND
  const ref = useRef<HTMLTableRowElement>(null);

  const [, drop] = useDrop({
    accept: "ROW",
    drop(item: { flatIndex: number; draggedRow: InstoreOrderLineTableRow }) {
      const from = item.flatIndex;
      const to = row.original.flatIndex!;
      if (moveRow && from !== to) {
        moveRow(to, item.draggedRow);
      }
    },
    canDrop: () => Boolean(moveRow),
  });

  const [{ isDragging }, drag] = useDrag({
    type: "ROW",
    canDrag: () => Boolean(moveRow) && row.getIsExpanded(),
    item: () => ({
      flatIndex: row.original.flatIndex,
      draggedRow: row.original,
    }),
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });

  if (moveRow) {
    drag(drop(ref));
  }
  //#endregion

  //#region RENDER
  return (
    <tr
      ref={ref}
      data-index={virtualRow.index}
      key={row.id}
      className={classNames(
        row.original.parent ? "bg-gray-100" : "",
        row.original.placeholder ? "border-amber-600" : "border-slate-300",
        "border-t h-12 w-full flex items-center absolute"
      )}
      style={{
        transform: `translateY(${virtualRow.start}px)`,
        height: "40px",
        opacity: isDragging ? 0.4 : 1,
      }}
    >
      {row.getVisibleCells().map((cell, i) => {
        const colSpan = row.original.parent && i === 1 ? 2 : 1;

        return (
          <td
            key={cell.id}
            colSpan={colSpan}
            className={classNames(
              row.original.parent
                ? "font-bold text-gray-800 text-sm"
                : row.original.placeholder
                ? "text-xs text-amber-800 animate-pulse"
                : "text-xs text-gray-600",
              // Now that proportions beat content width, over-long text has to be
              // cut off instead of running into the next column. Skipped for the
              // perso cell, whose tooltip is rendered inside it, and for theme
              // rows, whose title is supposed to run on.
              !row.original.parent &&
                cell.column.id !== "custom_fields" &&
                "overflow-hidden text-ellipsis",
              "whitespace-nowrap px-2 text-left sm:pl-0"
            )}
            // Columns scale proportionally to their `size` and fill the
            // container (same rule as the header, so they stay aligned).
            //
            // `minWidth: 0` forces the proportions to win over content width,
            // which is what keeps an article row in step with the header. Theme
            // rows are exempt: their one long title is meant to run on across the
            // empty cells next to it (what the `colSpan` above intends but can't
            // do in a flex layout), and being a grey section band they don't need
            // to line up with anything.
            style={{
              flexGrow: cell.column.getSize(),
              flexShrink: 1,
              flexBasis: 0,
              minWidth: row.original.parent ? undefined : 0,
            }}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </td>
        );
      })}
    </tr>
  );
  //#endregion
}
